import {
  AccessDenied,
  Error404,
  NoData,
  NotFound as NotFoundIcon,
} from 'phosphor-icons';
import { c } from '../../../utils/tailwind-merge';
import { ButtonV2 } from '../../atoms/Button/Button.v2';
import { LinkV2 } from '../../atoms/Link/Link.v2';
import { WriteReviewModal } from '../../organisms/WriteReviewModal';
import { emptyWriteReview } from '../../organisms/WriteReviewModal/WriteReviewModal';
import { NotFoundProps } from './NotFound.types';

export function NotFound({
  className,
  links,
  microcopy,
  variant = 'not-found',
  writeReviewModal,
  onSubmitReview,
  onEmailPublisher,
}: NotFoundProps) {
  const { title, description, linksHeading } = microcopy;
  const isPageNotFoundVariant = variant === 'page-not-found';
  const isNoFoundReviews = variant === 'no-reviews';
  const hasLinks = links && links.length > 0;
  const hasCtaLinks = !isPageNotFoundVariant && hasLinks;

  return (
    <section
      className={c(
        `group is-${variant}`,
        'gap-8 py-5 md:gap-10',
        'mx-auto flex w-full max-w-[600px] flex-col',
        { 'max-w-full md:flex-row-reverse': isPageNotFoundVariant },
        className
      )}
    >
      <div
        className={c({
          'basis-full md:basis-1/3': isPageNotFoundVariant,
        })}
      >
        <NotFoundIcon className="mx-auto hidden group-[.is-not-found]:block" />
        <NotFoundIcon className="mx-auto hidden group-[.is-no-reviews]:block" />
        <NoData className="mx-auto hidden group-[.is-no-data]:block" />
        <AccessDenied className="mx-auto hidden group-[.is-access-denied]:block" />
        <Error404 className="mx-auto hidden group-[.is-server-error]:block" />
        <Error404
          width={242}
          height={242}
          className="mx-auto hidden group-[.is-page-not-found]:block"
        />
      </div>
      <div
        className={c('flex flex-col gap-4', {
          'basis-full gap-5 md:basis-2/3': isPageNotFoundVariant,
          'text-center': isNoFoundReviews,
        })}
      >
        {isPageNotFoundVariant && (
          <div className="flex flex-col gap-5 pb-5">
            <h3 className="text-sm font-semibold uppercase leading-5">
              {title}
            </h3>
            <h2 className="text-2xl font-medium uppercase leading-8 tracking-[7.20px] md:text-4xl md:leading-10">
              Well, this is unexpected
            </h2>
          </div>
        )}
        <h1 className="text-xl font-semibold leading-6 md:text-2xl md:font-medium md:leading-8">
          {isPageNotFoundVariant ? description : title}
        </h1>
        <p className="text-base leading-5 md:leading-6">
          {isPageNotFoundVariant ? linksHeading : description}
        </p>
        {isPageNotFoundVariant && hasLinks && (
          <ul className="flex flex-col gap-4">
            {links.map((link) => (
              <li key={link.href}>
                <LinkV2
                  href={link.href}
                  target={link.openInNewTab ? '_blank' : '_self'}
                >
                  {link.value}
                </LinkV2>
              </li>
            ))}
          </ul>
        )}
        {isNoFoundReviews && (
          <div className="flex flex-col items-start gap-3">
            <WriteReviewModal
              title={writeReviewModal?.title || ''}
              imageUrl={writeReviewModal?.imageUrl || ''}
              version={writeReviewModal?.version || ''}
              ownedDate={writeReviewModal?.ownedDate || ''}
              isMultiPackage={writeReviewModal?.isMultiPackage || false}
              microcopy={microcopy.writeReviewModal || emptyWriteReview}
              onEmailPublisher={onEmailPublisher}
              onSubmitReview={onSubmitReview}
              reviewModalTrigger={undefined}
            />
          </div>
        )}
      </div>
      {hasCtaLinks && (
        <div
          role="group"
          className="flex flex-col items-center gap-3 md:flex-row md:items-start"
        >
          <ButtonV2
            size="medium"
            asChild
            className="w-80 md:w-fit"
            iconRight="ArrowRight"
          >
            <a
              href={links[0].href}
              target={links[0].openInNewTab ? '_blank' : '_self'}
              rel="noopener noreferrer"
            >
              {links[0].value}
            </a>
          </ButtonV2>
          {links[1] && (
            <ButtonV2
              size="medium"
              variant="tertiary-black-monochrome"
              asChild
              className="w-80 md:w-fit"
            >
              <a
                href={links[1].href}
                target={links[1].openInNewTab ? '_blank' : '_self'}
                rel="noopener noreferrer"
              >
                {links[1].value}
              </a>
            </ButtonV2>
          )}
        </div>
      )}
    </section>
  );
}
